// 🔄 신청서 상태 자동 계산 유틸리티
// 개별 기기 상태를 기반으로 전체 신청 상태와 가격을 자동 계산

import { DeviceInfo } from '../pages/admin/types/application';


/**
 * 🔄 기기별 상태를 기반으로 전체 신청 상태를 자동 계산 (새로운 상태 정의 적용)
 * @param devices 기기 배열
 * @returns 계산된 전체 상태
 */
export const calculateApplicationStatus = (devices: DeviceInfo[], manualStatus?: string): string => {
  if (!devices || devices.length === 0) {
    return '접수완료';
  }
  
  const statuses = devices.map(device => device.status);
  const uniqueStatuses = [...new Set(statuses)];
  
  // 🔄 관리자가 수동으로 설정한 상태가 있고, 기기별 처리 이전 단계인 경우 우선 적용
  if (manualStatus && ['접수완료', '택배접수', '입고완료'].includes(manualStatus)) {
    return manualStatus;
  }
  
  // 🔄 모든 기기가 초기 단계(접수완료/택배접수/입고완료) 상태인 경우 해당 상태 유지
  if (uniqueStatuses.length === 1 && ['접수완료', '택배접수', '입고완료'].includes(uniqueStatuses[0])) {
    return uniqueStatuses[0];
  }
  
  // 🟢 모든 기기가 완료된 경우
  if (uniqueStatuses.length === 1 && uniqueStatuses[0] === '완료') {
    return '완료';
  }
  
  // 🔴 모든 기기가 반송된 경우  
  if (uniqueStatuses.length === 1 && uniqueStatuses[0] === '반송') {
    return '반송';
  }
  
  // 🟡 일부 완료 + 일부 반송 (부분 처리)
  if (uniqueStatuses.includes('완료') && uniqueStatuses.includes('반송')) {
    return '부분완료';
  }
  
  // 🔵 모든 기기가 입금대기 상태
  if (uniqueStatuses.length === 1 && uniqueStatuses[0] === '입금대기') {
    return '입금대기';
  }
  
  // 🟠 가격안내 단계 (모든 기기가 가격안내일 때만 승격)
  if (uniqueStatuses.length === 1 && uniqueStatuses[0] === '가격안내') {
    return '가격안내';
  }
  
  // 🟦 검수 중 (하나라도 검수중 상태)
  if (uniqueStatuses.includes('검수중')) {
    return '검수중';
  }
  
  // 🟪 혼재된 상태들의 우선순위 처리
  // 완료 + 입금대기 → 부분완료 (일부는 입금됨, 일부는 대기중)
  if (uniqueStatuses.includes('완료') && uniqueStatuses.includes('입금대기')) {
    return '부분완료';
  }
  
  // 입금대기 + 반송 → 부분완료
  if (uniqueStatuses.includes('입금대기') && uniqueStatuses.includes('반송')) {
    return '부분완료'; 
  }
  
  // 기본값 (초기 상태)
  return '접수완료';
};

/**
 * 기기별 가격을 합산하여 전체 가격을 계산
 * @param devices 기기 배열
 * @returns 계산된 전체 가격 정보
 */
export const calculateTotalPrices = (devices: DeviceInfo[]) => {
  if (!devices || devices.length === 0) {
    return {
      total_price_offer: 0,
      total_final_price: 0
    };
  }
  
  let total_price_offer = 0;
  let total_final_price = 0;
  
  devices.forEach(device => {
    if (device.price_offer && !isNaN(Number(device.price_offer))) {
      total_price_offer += Number(device.price_offer);
    }
    // ✅ 요구사항: 기기 상태가 '완료'인데 final_price가 없거나 0이면 price_offer로 보정
    if (device.status === '완료') {
      if (device.final_price && !isNaN(Number(device.final_price)) && Number(device.final_price) > 0) {
        total_final_price += Number(device.final_price);
      } else if (device.price_offer && !isNaN(Number(device.price_offer))) {
        total_final_price += Number(device.price_offer);
      }
    } else {
      if (device.final_price && !isNaN(Number(device.final_price))) {
        total_final_price += Number(device.final_price);
      }
    }
  });
    
  return {
    total_price_offer,
    total_final_price
  };
};

/**
 * 🔄 기기 상태별 개수를 계산 (새로운 상태 정의 적용)
 * @param devices 기기 배열
 * @returns 상태별 통계
 */
export const calculateDeviceStatusStats = (devices: DeviceInfo[]) => {
  if (!devices || devices.length === 0) {
    return {
      total: 0,
      접수완료: 0,
      택배접수: 0,
      입고완료: 0,
      검수중: 0,
      가격안내: 0,
      입금대기: 0,
      반송: 0,
      완료: 0
    };
  }
  
  const stats = {
    total: devices.length,
    접수완료: 0,
    택배접수: 0,
    입고완료: 0,
    검수중: 0,
    가격안내: 0,
    입금대기: 0,
    반송: 0,
    완료: 0
  };
  
  devices.forEach(device => {
    switch (device.status) {
      case '접수완료':
        stats.접수완료++;
        break;
      case '택배접수':
        stats.택배접수++;
        break;
      case '입고완료':
        stats.입고완료++;
        break;
      case '검수중':
        stats.검수중++;
        break;
      case '가격안내':
        stats.가격안내++;
        break;
      case '입금대기':
        stats.입금대기++;
        break;
      case '반송':
        stats.반송++;
        break;
      case '완료':
        stats.완료++;
        break;
    }
  });
  
  return stats;
};

/**
 * 신청서 상태 변경이 필요한지 확인
 * @param currentStatus 현재 전체 상태
 * @param devices 기기 배열
 * @returns 상태 변경 필요 여부와 새로운 상태
 */
export const shouldUpdateApplicationStatus = (
  currentStatus: string, 
  devices: DeviceInfo[]
): { shouldUpdate: boolean; newStatus: string } => {
  const calculatedStatus = calculateApplicationStatus(devices);
  
  return {
    shouldUpdate: currentStatus !== calculatedStatus,
    newStatus: calculatedStatus
  };
};

/**
 * 🔄 기기 상태 변경 시 유효성 검증 (새로운 상태 정의 적용)
 * @param device 변경할 기기 정보
 * @param newStatus 새로운 상태
 * @returns 유효성 검증 결과
 */
export const validateDeviceStatusChange = (
  device: DeviceInfo, 
  newStatus: DeviceInfo['status']
): { isValid: boolean; error?: string } => {
  // 완료된 기기는 다른 상태로 변경 불가
  //if (device.status === '완료' && newStatus !== '완료') {
  //  return {
  //    isValid: false,
  //    error: '완료된 기기는 다른 상태로 변경할 수 없습니다.'
  //  };
  //}
  
  // 🎯 사용자 요구사항: 검수 가격 및 최종 가격은 아무 상태에서나 입력 및 변경이 가능해야 함
  // 따라서 가격 입력 필수 조건은 제거함
  
  // 반송 상태로 변경 시 반송 사유 권장 (경고만, 차단하지 않음)
  if (newStatus === '반송' && !device.reject_reason) {
    console.warn('반송 사유를 입력하는 것을 권장합니다.');
  }
  
  // 🔄 초기 단계(접수완료/택배접수/입고완료/검수중)는 자유롭게 변경 가능
  if (['접수완료', '택배접수', '입고완료', '검수중'].includes(newStatus)) {
    return { isValid: true };
  }
  
  // 상태 변경 순서 검증 (비즈니스 로직) - 초기 단계 포함
  const statusOrder = ['접수완료', '택배접수', '입고완료', '검수중', '가격안내', '입금대기', '완료'];
  const currentIndex = statusOrder.indexOf(device.status);
  const newIndex = statusOrder.indexOf(newStatus);
  
  // 반송은 언제든 가능 (예외 처리)
  if (newStatus === '반송') {
    return { isValid: true };
  }
  
  // 정상적인 순서가 아닌 경우 경고 (차단하지는 않음)
  if (currentIndex >= 0 && newIndex >= 0 && newIndex < currentIndex) {
    console.warn(`상태가 역순으로 변경됩니다: ${device.status} → ${newStatus}`);
  }
  
  return { isValid: true };
};

/**
 * 신청서 전체 데이터 동기화 (상태와 가격을 일관성있게 업데이트)
 * @param devices 기기 배열
 * @returns 동기화된 신청서 데이터
 */
export const synchronizeApplicationData = (devices: DeviceInfo[]) => {
  const status = calculateApplicationStatus(devices);
  const { total_price_offer, total_final_price } = calculateTotalPrices(devices);
  const stats = calculateDeviceStatusStats(devices);
  
  // ✅ 요구사항: 상태가 '완료'인데 total_final_price가 0이면 total_price_offer로 보정
  let effective_total_final_price = total_final_price;
  if (status === '완료' && (!effective_total_final_price || effective_total_final_price === 0)) {
    effective_total_final_price = total_price_offer;
  }
  
  return {
    status,
    total_price_offer,
    total_final_price: effective_total_final_price,
    // 하위 호환성을 위한 기존 필드
    price_offer: total_price_offer,
    final_price: effective_total_final_price,
    stats,
    updated_at: new Date()
  };
};